import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import StaffService from "../../services/Staff/StaffService";
import {
  Users,
  Mail,
  Shield,
  UserCheck,
  Search,
  RotateCw,
  Download,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Inbox,
  Briefcase,
  User,
  Edit2,
  Trash2,
  AlertTriangle,
  Phone,
  CreditCard,
  Send,
  Eye,
  FileText,
  CheckCircle2,
  KeyRound
} from "lucide-react";
import { Toast } from "../ui/Toast";
import { Badge } from "../ui/Badge";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import { Switch } from "../ui/Switch";
import { Tooltip } from "../ui/Tooltip";

function maskCPF(value) {
  if (!value) return "";
  let v = String(value).replace(/\D/g, "");
  if (v.length > 11) v = v.substring(0, 11);
  if (v.length > 9) {
    return v.replace(/^(\d{3})(\d{3})(\d{3})(\d{1,2})/, "$1.$2.$3-$4");
  } else if (v.length > 6) {
    return v.replace(/^(\d{3})(\d{3})(\d{1,3})/, "$1.$2.$3");
  } else if (v.length > 3) {
    return v.replace(/^(\d{3})(\d{1,3})/, "$1.$2");
  }
  return v;
}

function maskPhone(value) {
  if (!value) return "";
  let v = String(value).replace(/\D/g, "");
  if (v.length > 11) v = v.substring(0, 11);
  if (v.length > 10) {
    return v.replace(/^(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  } else if (v.length > 6) {
    return v.replace(/^(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
  } else if (v.length > 2) {
    return v.replace(/^(\d{2})(\d{0,5})/, "($1) $2");
  }
  return v;
}

function extractTipoUsuario(val) {
  if (!val) return "corredor";
  if (typeof val === "object" && val !== null) {
    return val.value || val.label || val.name || "corredor";
  }
  return String(val);
}

function StaffUsuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);
  const [paginaInput, setPaginaInput] = useState("1");

  // Estados para Edição
  const [modalEditarAberto, setModalEditarAberto] = useState(false);
  const [usuarioEditando, setUsuarioEditando] = useState(null);
  const [carregandoDadosUsuario, setCarregandoDadosUsuario] = useState(false);
  const [formEdit, setFormEdit] = useState({
    first_name: "",
    last_name: "",
    email: "",
    tipo_usuario: "corredor",
    cpf: "",
    telefone: "",
    is_active: true
  });
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);

  // Estados para Exclusão
  const [modalDeletarAberto, setModalDeletarAberto] = useState(false);
  const [usuarioDeletando, setUsuarioDeletando] = useState(null);
  const [deletandoConta, setDeletandoConta] = useState(false);

  // Estados para Reset de Senha
  const [modalResetAberto, setModalResetAberto] = useState(false);
  const [usuarioReset, setUsuarioReset] = useState(null);
  const [enviandoReset, setEnviandoReset] = useState(false);

  // Estados para Disparo Geral de E-mail
  const [modalEmailAberto, setModalEmailAberto] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailAudience, setEmailAudience] = useState("todos");
  const [abaEmail, setAbaEmail] = useState("escrever");
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [confirmouEnvioGeral, setConfirmouEnvioGeral] = useState(false);

  const toastRef = useRef(null);
  const staffService = useRef(new StaffService()).current;

  const carregarUsuarios = useCallback(async () => {
    setLoading(true);
    try {
      const res = await staffService.getUsuarios();
      if (res.ok) {
        const data = await res.json();
        const lista = Array.isArray(data) ? data : (data?.results || data?.data || []);
        setUsuarios(lista);
      } else {
        toastRef.current?.show({
          severity: "error",
          summary: "Erro",
          detail: `Não foi possível carregar os usuários (${res.status})`
        });
      }
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
      toastRef.current?.show({
        severity: "error",
        summary: "Falha de Conexão",
        detail: "Ocorreu um erro ao consultar o servidor."
      });
    } finally {
      setLoading(false);
    }
  }, [staffService]);

  useEffect(() => {
    carregarUsuarios();
  }, [carregarUsuarios]);

  // Abertura do Modal de Edição
  const handleAbrirEditar = async (user) => {
    setUsuarioEditando(user);
    setFormEdit({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      tipo_usuario: extractTipoUsuario(user.tipo_usuario),
      cpf: maskCPF(user.cpf || ""),
      telefone: maskPhone(user.telefone || user.telefone1 || user.celular || ""),
      is_active: user.is_active !== false
    });
    setModalEditarAberto(true);

    // 1. Primeiro recupera as informações do usuário (/core/user_credentials/<id>/)
    // 2. E após, recupera as informações adicionais (/core/info_adicional_usuario/retrieve/<usuario_id>/)
    if (user?.id) {
      setCarregandoDadosUsuario(true);
      try {
        // Passo 1: Informações do usuário
        try {
          const userRes = await staffService.getUsuarioById(user.id);
          if (userRes.ok) {
            const userData = await userRes.json();
            const userItem = Array.isArray(userData) ? userData[0] : (userData?.results?.[0] || userData);
            if (userItem) {
              setFormEdit((prev) => ({
                ...prev,
                first_name: userItem.first_name || prev.first_name,
                last_name: userItem.last_name || prev.last_name,
                email: userItem.email || prev.email,
                tipo_usuario: extractTipoUsuario(userItem.tipo_usuario || prev.tipo_usuario),
                cpf: maskCPF(userItem.cpf || prev.cpf),
                telefone: maskPhone(userItem.telefone || userItem.telefone1 || userItem.celular || prev.telefone),
                is_active: userItem.is_active !== undefined ? userItem.is_active : prev.is_active
              }));
            }
          }
        } catch (errUser) {
          console.warn("Aviso ao buscar dados do usuário:", errUser);
        }

        // Passo 2: Informações adicionais do usuário (CPF e telefone)
        try {
          const infoRes = await staffService.getInfoAdicionalUsuario(user.id);
          if (infoRes.ok) {
            const infoData = await infoRes.json();
            const infoItem = Array.isArray(infoData) ? infoData[0] : (infoData?.results?.[0] || infoData);
            if (infoItem) {
              setFormEdit((prev) => ({
                ...prev,
                cpf: maskCPF(infoItem.cpf || prev.cpf),
                telefone: maskPhone(
                  infoItem.telefone ||
                  infoItem.telefone1 ||
                  infoItem.celular ||
                  prev.telefone
                )
              }));
            }
          } else {
            console.warn("Retorno não-200 ao buscar informações adicionais:", infoRes.status);
          }
        } catch (errInfo) {
          console.warn("Aviso ao buscar informações adicionais:", errInfo);
        }
      } finally {
        setCarregandoDadosUsuario(false);
      }
    }
  };

  const handleFecharEditar = () => {
    if (salvandoEdicao) return;
    setModalEditarAberto(false);
    setUsuarioEditando(null);
    setCarregandoDadosUsuario(false);
  };

  // Salvar Edição de Usuário
  const handleSalvarEdicao = async (e) => {
    if (e) e.preventDefault();
    if (!usuarioEditando) return;

    if (!formEdit.first_name.trim()) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "O primeiro nome é obrigatório."
      });
      return;
    }

    if (!formEdit.email.trim()) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "O e-mail é obrigatório."
      });
      return;
    }

    setSalvandoEdicao(true);
    const cleanCpf = (formEdit.cpf || "").replace(/\D/g, "");
    const cleanTelefone = (formEdit.telefone || "").replace(/\D/g, "");
    const cleanTipoUsuario = extractTipoUsuario(formEdit.tipo_usuario);
    try {
      const payload = {
        first_name: formEdit.first_name.trim(),
        last_name: formEdit.last_name.trim(),
        email: formEdit.email.trim(),
        tipo_usuario: cleanTipoUsuario,
        cpf: cleanCpf,
        telefone: cleanTelefone,
        telefone1: cleanTelefone,
        is_active: formEdit.is_active
      };

      const res = await staffService.atualizarUsuario(usuarioEditando.id, payload);

      // Atualiza também informações adicionais (CPF/Telefone) via endpoint exclusivo /core/user/update/<int:pk>/
      try {
        const infoAdicionalPayload = {
          cpf: cleanCpf,
          telefone: cleanTelefone,
          telefone1: cleanTelefone
        };
        const infoRes = await staffService.atualizarInfoAdicional(usuarioEditando.id, infoAdicionalPayload);
        if (!infoRes.ok) {
          const infoErr = await infoRes.json().catch(() => ({}));
          console.warn("Aviso: info adicional retornou erro", infoRes.status, infoErr);
        }
      } catch (errInfo) {
        console.error("Erro ao atualizar info adicional", errInfo);
      }

      if (res.ok || res.status === 200 || res.status === 204) {
        let dadosAtualizados = {};
        try {
          dadosAtualizados = await res.json();
        } catch {
          // Se não retornar JSON (ex: 204), usamos payload
        }

        setUsuarios((prev) =>
          prev.map((u) =>
            u.id === usuarioEditando.id
              ? {
                  ...u,
                  ...payload,
                  cpf: cleanCpf,
                  telefone: cleanTelefone,
                  telefone1: cleanTelefone,
                  ...dadosAtualizados
                }
              : u
          )
        );

        toastRef.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Usuário atualizado com sucesso!"
        });
        setModalEditarAberto(false);
        setUsuarioEditando(null);
      } else {
        const errorData = await res.json().catch(() => ({}));
        const errorMsg =
          errorData?.detail ||
          errorData?.message ||
          errorData?.erro ||
          Object.values(errorData)[0]?.[0] ||
          `Erro ao atualizar usuário (${res.status})`;
        toastRef.current?.show({
          severity: "error",
          summary: "Erro ao Atualizar",
          detail: String(errorMsg)
        });
      }
    } catch (error) {
      console.error("Erro ao atualizar usuário:", error);
      toastRef.current?.show({
        severity: "error",
        summary: "Falha de Conexão",
        detail: "Não foi possível conectar ao servidor para atualizar o usuário."
      });
    } finally {
      setSalvandoEdicao(false);
    }
  };

  // Abertura do Modal de Deletar
  const handleAbrirDeletar = (user) => {
    setUsuarioDeletando(user);
    setModalDeletarAberto(true);
  };

  const handleFecharDeletar = () => {
    if (deletandoConta) return;
    setModalDeletarAberto(false);
    setUsuarioDeletando(null);
  };

  // Confirmar Exclusão do Usuário
  const handleConfirmarDelecao = async () => {
    if (!usuarioDeletando) return;

    setDeletandoConta(true);
    try {
      const res = await staffService.deletarUsuario(usuarioDeletando.id);

      if (res.ok || res.status === 204 || res.status === 200) {
        setUsuarios((prev) => prev.filter((u) => u.id !== usuarioDeletando.id));
        toastRef.current?.show({
          severity: "success",
          summary: "Conta Excluída",
          detail: `A conta de "${usuarioDeletando.first_name || usuarioDeletando.username || usuarioDeletando.email}" foi excluída com sucesso.`
        });
        setModalDeletarAberto(false);
        setUsuarioDeletando(null);
      } else {
        const errData = await res.json().catch(() => ({}));
        let errorMsg =
          errData?.detail ||
          errData?.erro ||
          errData?.message ||
          `Não foi possível excluir a conta (${res.status})`;

        const msgString = String(errorMsg).toLowerCase();
        if (
          msgString.includes("foreign key constraint") || 
          msgString.includes("integrityerror") || 
          res.status === 500
        ) {
          errorMsg = "Este usuário possui vínculos no sistema (ex: inscrições, informações adicionais) e não pode ser excluído permanentemente. Sugerimos editar e inativar a conta.";
        }

        toastRef.current?.show({
          severity: "error",
          summary: "Erro ao Excluir",
          detail: String(errorMsg)
        });
      }
    } catch (error) {
      console.error("Erro ao deletar usuário:", error);
      toastRef.current?.show({
        severity: "error",
        summary: "Falha de Conexão",
        detail: "Não foi possível conectar ao servidor para deletar o usuário."
      });
    } finally {
      setDeletandoConta(false);
    }
  };

  // Abertura do Modal de Reset de Senha
  const handleAbrirResetSenha = (user) => {
    setUsuarioReset(user);
    setModalResetAberto(true);
  };

  const handleFecharResetSenha = () => {
    if (enviandoReset) return;
    setModalResetAberto(false);
    setUsuarioReset(null);
  };

  // Confirmar Solicitação de Reset de Senha
  const handleConfirmarResetSenha = async () => {
    if (!usuarioReset) return;

    if (!usuarioReset.email) {
      toastRef.current?.show({
        severity: "warn",
        summary: "E-mail Ausente",
        detail: "Este usuário não possui um endereço de e-mail cadastrado para receber o link de redefinição."
      });
      return;
    }

    setEnviandoReset(true);
    try {
      const tipo = extractTipoUsuario(usuarioReset.tipo_usuario);
      const res = await staffService.solicitarResetSenha(usuarioReset.email, tipo);

      if (res.ok || res.status === 200 || res.status === 201 || res.status === 204) {
        const data = await res.json().catch(() => ({}));
        toastRef.current?.show({
          severity: "success",
          summary: "E-mail Enviado!",
          detail: data?.detalhe || `Instruções de redefinição de senha enviadas com sucesso para ${usuarioReset.email}.`
        });
        setModalResetAberto(false);
        setUsuarioReset(null);
      } else {
        const errData = await res.json().catch(() => ({}));
        const errorMsg =
          errData?.email?.[0] ||
          errData?.detail ||
          errData?.detalhe ||
          errData?.erro ||
          errData?.message ||
          `Não foi possível enviar o reset de senha (${res.status}).`;

        toastRef.current?.show({
          severity: "error",
          summary: "Erro ao Redefinir Senha",
          detail: String(errorMsg)
        });
      }
    } catch (error) {
      console.error("Erro ao solicitar reset de senha:", error);
      toastRef.current?.show({
        severity: "error",
        summary: "Falha de Conexão",
        detail: "Não foi possível conectar ao servidor para solicitar a redefinição de senha."
      });
    } finally {
      setEnviandoReset(false);
    }
  };

  const getTipoBadge = (tipo) => {
    const val = String(tipo || "").toLowerCase();
    if (val.includes("staff") || val.includes("admin")) {
      return (
        <Badge variant="danger" size="sm">
          <Shield className="w-3 h-3 mr-1 inline" />
          {tipo || "Staff"}
        </Badge>
      );
    }
    if (val.includes("organizador")) {
      return (
        <Badge variant="warning" size="sm">
          <Briefcase className="w-3 h-3 mr-1 inline" />
          {tipo || "Organizador"}
        </Badge>
      );
    }
    return (
      <Badge variant="success" size="sm">
        <User className="w-3 h-3 mr-1 inline" />
        {tipo || "Corredor"}
      </Badge>
    );
  };

  const formatDate = (val) => {
    if (!val) return "—";
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return String(val);
      return d.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return String(val);
    }
  };

  // Estatísticas rápidas
  const totalUsuarios = usuarios.length;
  const totalOrganizadores = usuarios.filter((u) =>
    String(u.tipo_usuario || "").toLowerCase().includes("organizador")
  ).length;
  const totalCorredores = usuarios.filter((u) =>
    String(u.tipo_usuario || "").toLowerCase().includes("corredor")
  ).length;
  const totalStaff = usuarios.filter((u) =>
    String(u.tipo_usuario || "").toLowerCase().includes("staff")
  ).length;

  // Filtragem
  const dadosFiltrados = useMemo(() => {
    if (!globalFilter.trim()) return usuarios;
    const term = globalFilter.toLowerCase();
    return usuarios.filter((u) =>
      (u.username || "").toLowerCase().includes(term) ||
      (u.first_name || "").toLowerCase().includes(term) ||
      (u.last_name || "").toLowerCase().includes(term) ||
      (u.email || "").toLowerCase().includes(term) ||
      (u.cpf || "").toLowerCase().includes(term) ||
      (u.tipo_usuario || "").toLowerCase().includes(term) ||
      String(u.id || "").includes(term)
    );
  }, [usuarios, globalFilter]);

  // Validação unificada de usuário ativo com e-mail válido
  const isUsuarioComEmailValido = useCallback((u) => {
    const email = String(u?.email || "").trim();
    return email.length > 3 && email.includes("@") && u?.is_active !== false;
  }, []);

  // Destinatários para Disparo Geral de E-mail
  const destinatariosAlvo = useMemo(() => {
    if (emailAudience === "filtrados") {
      return dadosFiltrados.filter(isUsuarioComEmailValido);
    }
    if (emailAudience === "corredor") {
      return usuarios.filter(
        (u) => isUsuarioComEmailValido(u) && String(u.tipo_usuario || "").toLowerCase().includes("corredor")
      );
    }
    if (emailAudience === "organizador") {
      return usuarios.filter(
        (u) => isUsuarioComEmailValido(u) && String(u.tipo_usuario || "").toLowerCase().includes("organizador")
      );
    }
    if (emailAudience === "staff") {
      return usuarios.filter(
        (u) => isUsuarioComEmailValido(u) && String(u.tipo_usuario || "").toLowerCase().includes("staff")
      );
    }
    // "todos": todos os usuários ativos com e-mail
    return usuarios.filter(isUsuarioComEmailValido);
  }, [usuarios, dadosFiltrados, emailAudience, isUsuarioComEmailValido]);

  const totalAtivosComEmail = useMemo(
    () => usuarios.filter(isUsuarioComEmailValido).length,
    [usuarios, isUsuarioComEmailValido]
  );
  const totalCorredoresComEmail = useMemo(
    () =>
      usuarios.filter(
        (u) =>
          isUsuarioComEmailValido(u) &&
          String(u.tipo_usuario || "").toLowerCase().includes("corredor")
      ).length,
    [usuarios, isUsuarioComEmailValido]
  );
  const totalOrganizadoresComEmail = useMemo(
    () =>
      usuarios.filter(
        (u) =>
          isUsuarioComEmailValido(u) &&
          String(u.tipo_usuario || "").toLowerCase().includes("organizador")
      ).length,
    [usuarios, isUsuarioComEmailValido]
  );
  const totalStaffComEmail = useMemo(
    () =>
      usuarios.filter(
        (u) =>
          isUsuarioComEmailValido(u) &&
          String(u.tipo_usuario || "").toLowerCase().includes("staff")
      ).length,
    [usuarios, isUsuarioComEmailValido]
  );
  const totalFiltradosComEmail = useMemo(
    () => dadosFiltrados.filter(isUsuarioComEmailValido).length,
    [dadosFiltrados, isUsuarioComEmailValido]
  );

  const handleAbrirModalEmail = () => {
    setEmailSubject("");
    setEmailMessage("");
    setEmailAudience("todos");
    setAbaEmail("escrever");
    setConfirmouEnvioGeral(false);
    setModalEmailAberto(true);
  };

  const resetarModalEmail = () => {
    setModalEmailAberto(false);
    setEmailSubject("");
    setEmailMessage("");
    setConfirmouEnvioGeral(false);
    setAbaEmail("escrever");
  };

  const handleFecharModalEmail = () => {
    if (enviandoEmail) return;
    resetarModalEmail();
  };

  const escapeHtml = (unsafe) =>
    String(unsafe || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const formatMessageHtml = (text) => {
    const paragraphs = String(text || "").split(/\n\s*\n/);
    return paragraphs
      .map((p) => `<p style="margin: 0 0 16px 0; line-height: 1.6;">${escapeHtml(p).replace(/\n/g, "<br/>")}</p>`)
      .join("");
  };

  const handleEnviarEmailGeral = async (e) => {
    if (e) e.preventDefault();

    if (!emailSubject.trim()) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "Por favor, informe o assunto do e-mail."
      });
      return;
    }

    if (!emailMessage.trim()) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "Por favor, digite o conteúdo da mensagem a ser enviada."
      });
      return;
    }

    if (destinatariosAlvo.length === 0) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Nenhum Destinatário",
        detail: "Não há usuários válidos com e-mail cadastrado no público selecionado."
      });
      return;
    }

    setEnviandoEmail(true);

    try {
      const userIds = destinatariosAlvo.map((u) => u.id);
      const payload = {
        subject: emailSubject.trim(),
        assunto: emailSubject.trim(),
        message_html: formatMessageHtml(emailMessage.trim()),
        message_text: emailMessage.trim(),
        mensagem: emailMessage.trim(),
        tipo_usuario: emailAudience,
        user_ids: userIds
      };

      const response = await staffService.dispararEmailGeral(payload);
      const resData = await response.json().catch(() => ({}));

      if (response.ok || response.status === 200 || response.status === 202) {
        toastRef.current?.show({
          severity: "success",
          summary: "Disparo Geral Agendado!",
          detail: resData.mensagem || `E-mails enfileirados com sucesso para ${destinatariosAlvo.length} usuários.`,
          life: 5000
        });
        resetarModalEmail();
      } else {
        const errorMsg =
          resData.erro ||
          resData.detail ||
          resData.mensagem ||
          resData.subject ||
          resData.message_html ||
          `Falha ao disparar e-mails (${response.status})`;
        toastRef.current?.show({
          severity: "error",
          summary: "Erro no Disparo",
          detail: String(Array.isArray(errorMsg) ? errorMsg.join(" ") : errorMsg),
          life: 5000
        });
      }
    } catch (error) {
      console.error("Erro ao disparar e-mails gerais:", error);
      toastRef.current?.show({
        severity: "error",
        summary: "Erro de Conexão",
        detail: "Não foi possível conectar ao servidor para agendar o disparo de e-mails.",
        life: 5000
      });
    } finally {
      setEnviandoEmail(false);
    }
  };

  // Paginação
  const totalPaginas = Math.ceil(dadosFiltrados.length / itensPorPagina) || 1;
  const inicioIndice = (paginaAtual - 1) * itensPorPagina;
  const dadosPaginados = dadosFiltrados.slice(inicioIndice, inicioIndice + itensPorPagina);
  const totalMostrado = dadosFiltrados.length;
  const inicioItem = totalMostrado > 0 ? inicioIndice + 1 : 0;
  const fimItem = Math.min(inicioIndice + itensPorPagina, totalMostrado);

  // Ajusta página atual se ultrapassar o total de páginas (ex: filtro ou alteração de itens por página)
  useEffect(() => {
    if (paginaAtual > totalPaginas) {
      setPaginaAtual(Math.max(1, totalPaginas));
    }
  }, [paginaAtual, totalPaginas]);

  // Sincroniza o campo de salto de página
  useEffect(() => {
    setPaginaInput(String(paginaAtual));
  }, [paginaAtual]);

  // Cálculo inteligente das páginas visíveis com elipses (...) para evitar estourar a tela com 500+ páginas
  const paginasVisiveis = useMemo(() => {
    if (totalPaginas <= 7) {
      return Array.from({ length: totalPaginas }, (_, i) => i + 1);
    }

    if (paginaAtual <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPaginas];
    }

    if (paginaAtual >= totalPaginas - 3) {
      return [
        1,
        "...",
        totalPaginas - 4,
        totalPaginas - 3,
        totalPaginas - 2,
        totalPaginas - 1,
        totalPaginas
      ];
    }

    return [
      1,
      "...",
      paginaAtual - 1,
      paginaAtual,
      paginaAtual + 1,
      "...",
      totalPaginas
    ];
  }, [paginaAtual, totalPaginas]);

  const handleIrParaPagina = (valor) => {
    const num = parseInt(valor, 10);
    if (!isNaN(num) && num >= 1 && num <= totalPaginas) {
      setPaginaAtual(num);
    } else {
      setPaginaInput(String(paginaAtual));
    }
  };

  const exportarCSV = () => {
    if (dadosFiltrados.length === 0) return;
    const cabecalho = "ID;Username;Nome;Email;Tipo;Telefone;Data Cadastro;Status\n";
    const linhas = dadosFiltrados
      .map(
        (u) =>
          `"${u.id}";"${u.username || ""}";"${[u.first_name, u.last_name].filter(Boolean).join(" ") || ""}";"${u.email || ""}";"${u.tipo_usuario || ""}";"${u.telefone || u.celular || ""}";"${formatDate(u.date_joined || u.created_at || u.data_cadastro)}";"${u.is_active === false ? "Inativo" : "Ativo"}"`
      )
      .join("\n");
    const blob = new Blob([cabecalho + linhas], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `staff_usuarios_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toastRef.current?.show({
      severity: "success",
      summary: "Sucesso",
      detail: "Relatório de usuários gerado com sucesso!"
    });
  };

  return (
    <div className="flex flex-col gap-6 w-full text-urb-text">
      <Toast ref={toastRef} />

      {/* Cards de Métricas de Usuários */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-urb-card border border-urb-border rounded-2xl p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-urb-primary/10 border border-urb-primary/20 flex items-center justify-center shrink-0">
            <Users className="w-5 h-5 text-urb-primary" />
          </div>
          <div>
            <span className="text-xs text-slate-400 font-medium">Total de Usuários</span>
            <div className="text-xl font-bold text-white mt-0.5">
              {loading ? <div className="h-6 w-12 bg-slate-800 rounded animate-pulse"></div> : totalUsuarios}
            </div>
          </div>
        </div>

        <div className="bg-urb-card border border-urb-border rounded-2xl p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
            <UserCheck className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <span className="text-xs text-slate-400 font-medium">Corredores</span>
            <div className="text-xl font-bold text-white mt-0.5">
              {loading ? <div className="h-6 w-12 bg-slate-800 rounded animate-pulse"></div> : totalCorredores}
            </div>
          </div>
        </div>

        <div className="bg-urb-card border border-urb-border rounded-2xl p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
            <Users className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <span className="text-xs text-slate-400 font-medium">Organizadores</span>
            <div className="text-xl font-bold text-white mt-0.5">
              {loading ? <div className="h-6 w-12 bg-slate-800 rounded animate-pulse"></div> : totalOrganizadores}
            </div>
          </div>
        </div>

        <div className="bg-urb-card border border-urb-border rounded-2xl p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center shrink-0">
            <Shield className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <span className="text-xs text-slate-400 font-medium">Equipe Staff</span>
            <div className="text-xl font-bold text-white mt-0.5">
              {loading ? <div className="h-6 w-12 bg-slate-800 rounded animate-pulse"></div> : totalStaff}
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Usuários */}
      <div className="bg-urb-card border border-urb-border rounded-2xl p-4 sm:p-6 shadow-sm">
        {/* Barra superior */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="relative flex items-center w-full sm:w-80">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 pointer-events-none" />
            <input
              type="text"
              value={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
                setPaginaAtual(1);
              }}
              placeholder="Buscar por nome, email, CPF..."
              className="w-full pl-9 pr-8 py-2 bg-urb-card-light border border-urb-border rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-urb-primary/60 transition-colors"
            />
            {globalFilter && (
              <button
                type="button"
                onClick={() => setGlobalFilter("")}
                className="absolute right-2.5 text-slate-400 hover:text-white bg-transparent border-none cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Tooltip content="Disparar e-mail geral para todos os usuários ou grupos selecionados">
              <button
                type="button"
                onClick={handleAbrirModalEmail}
                disabled={loading || usuarios.length === 0}
                className="inline-flex items-center gap-2 px-3 py-2 bg-urb-primary hover:bg-urb-primary/90 text-black border border-urb-primary rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send className="w-3.5 h-3.5" />
                <span>Disparo Geral de E-mail</span>
              </button>
            </Tooltip>
            <Tooltip content="Recarregar lista de usuários">
              <button
                type="button"
                onClick={carregarUsuarios}
                disabled={loading}
                className="inline-flex items-center gap-2 px-3 py-2 bg-urb-card-light hover:bg-urb-card border border-urb-border hover:border-urb-border/80 rounded-xl text-xs font-semibold text-slate-300 hover:text-white transition-colors cursor-pointer"
              >
                <RotateCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                <span>Atualizar</span>
              </button>
            </Tooltip>
            <Tooltip content="Exportar usuários filtrados para arquivo CSV">
              <button
                type="button"
                onClick={exportarCSV}
                disabled={loading || usuarios.length === 0}
                className="inline-flex items-center gap-2 px-3 py-2 bg-urb-primary/10 hover:bg-urb-primary/20 border border-urb-primary/30 rounded-xl text-xs font-semibold text-urb-primary transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Exportar CSV</span>
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Tabela de Dados */}
        <div className="overflow-x-auto rounded-xl border border-urb-border/50">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-urb-border bg-urb-card-light/40 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                <th className="py-3 px-3 w-16">ID</th>
                <th className="py-3 px-4">Usuário / Nome</th>
                <th className="py-3 px-4">E-mail</th>
                <th className="py-3 px-4 text-center">Perfil / Tipo</th>
                <th className="py-3 px-4">Telefone</th>
                <th className="py-3 px-4">Cadastrado em</th>
                <th className="py-3 px-4 text-center">Status</th>
                <th className="py-3 px-4 text-center">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-urb-border/30">
              {loading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    <td className="py-3 px-3"><div className="h-5 w-8 bg-slate-800 rounded"></div></td>
                    <td className="py-3 px-4"><div className="h-5 w-32 bg-slate-800 rounded"></div></td>
                    <td className="py-3 px-4"><div className="h-5 w-36 bg-slate-800 rounded"></div></td>
                    <td className="py-3 px-4 text-center"><div className="h-5 w-20 bg-slate-800 rounded mx-auto"></div></td>
                    <td className="py-3 px-4"><div className="h-5 w-24 bg-slate-800 rounded"></div></td>
                    <td className="py-3 px-4"><div className="h-5 w-24 bg-slate-800 rounded"></div></td>
                    <td className="py-3 px-4 text-center"><div className="h-5 w-14 bg-slate-800 rounded mx-auto"></div></td>
                    <td className="py-3 px-4 text-center"><div className="h-5 w-16 bg-slate-800 rounded mx-auto"></div></td>
                  </tr>
                ))
              ) : dadosPaginados.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-slate-400">
                    <Inbox className="w-10 h-10 mx-auto mb-2 text-slate-500" />
                    <p className="text-sm">Nenhum usuário encontrado.</p>
                  </td>
                </tr>
              ) : (
                dadosPaginados.map((row) => (
                  <tr key={row.id} className="hover:bg-urb-card-light/40 transition-colors">
                    <td className="py-3 px-3 text-xs text-slate-500 font-mono">
                      #{row.id}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col">
                        <span className="font-semibold text-white">
                          {[row.first_name, row.last_name].filter(Boolean).join(" ") || row.username || "—"}
                        </span>
                        {row.first_name && (
                          <span className="text-xs text-slate-400">@{row.username}</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5 text-slate-300">
                        <Mail className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                        <span>{row.email || "—"}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {getTipoBadge(row.tipo_usuario)}
                    </td>
                    <td className="py-3 px-4 text-slate-300">
                      {row.telefone || row.celular || "—"}
                    </td>
                    <td className="py-3 px-4 text-slate-400 text-xs">
                      {formatDate(row.date_joined || row.created_at || row.data_cadastro)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${
                          row.is_active === false
                            ? "bg-red-500/10 text-red-400 border border-red-500/20"
                            : "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                        }`}
                      >
                        {row.is_active === false ? "Inativo" : "Ativo"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <Tooltip content="Resetar senha do usuário" position="top">
                          <button
                            type="button"
                            onClick={() => handleAbrirResetSenha(row)}
                            aria-label="Resetar Senha"
                            className="p-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 hover:text-amber-300 border border-amber-500/20 transition-colors cursor-pointer"
                          >
                            <KeyRound className="w-3.5 h-3.5" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Editar dados e perfil do usuário" position="top">
                          <button
                            type="button"
                            onClick={() => handleAbrirEditar(row)}
                            aria-label="Editar Usuário"
                            className="p-1.5 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 border border-blue-500/20 transition-colors cursor-pointer"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Excluir conta permanentemente" position="top">
                          <button
                            type="button"
                            onClick={() => handleAbrirDeletar(row)}
                            aria-label="Excluir Conta"
                            className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 border border-red-500/20 transition-colors cursor-pointer"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer com Paginação */}
        {!loading && dadosFiltrados.length > 0 && (
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4 pt-4 mt-3 border-t border-urb-border/40">
            {/* Informações e Seletor de Itens por Página */}
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
              <span>
                Mostrando <span className="font-semibold text-white">{inicioItem}</span> a{" "}
                <span className="font-semibold text-white">{fimItem}</span> de{" "}
                <span className="font-semibold text-white">{totalMostrado}</span> usuários
              </span>

              <span className="text-urb-border hidden sm:inline">|</span>

              <div className="flex items-center gap-2">
                <span>Por página:</span>
                <select
                  value={itensPorPagina}
                  onChange={(e) => {
                    setItensPorPagina(Number(e.target.value));
                    setPaginaAtual(1);
                  }}
                  className="bg-urb-card-light border border-urb-border text-white text-xs rounded-lg px-2.5 py-1 focus:outline-none focus:border-urb-primary/60 transition-colors cursor-pointer"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            {/* Controles de Navegação */}
            <div className="flex flex-wrap items-center justify-center gap-2">
              <div className="flex items-center gap-1">
                {/* Primeira Página */}
                <Tooltip content="Primeira página" position="top">
                  <button
                    type="button"
                    onClick={() => setPaginaAtual(1)}
                    disabled={paginaAtual === 1}
                    aria-label="Primeira página"
                    className="w-8 h-8 rounded-lg flex items-center justify-center bg-urb-card-light border border-urb-border text-slate-300 hover:text-white hover:border-urb-primary/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </button>
                </Tooltip>

                {/* Página Anterior */}
                <Tooltip content="Página anterior" position="top">
                  <button
                    type="button"
                    onClick={() => setPaginaAtual((prev) => Math.max(prev - 1, 1))}
                    disabled={paginaAtual === 1}
                    aria-label="Página anterior"
                    className="w-8 h-8 rounded-lg flex items-center justify-center bg-urb-card-light border border-urb-border text-slate-300 hover:text-white hover:border-urb-primary/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                </Tooltip>

                {/* Páginas Numéricas com Elipse */}
                {paginasVisiveis.map((pg, idx) =>
                  pg === "..." ? (
                    <span
                      key={`dots-${idx}`}
                      className="w-8 h-8 flex items-center justify-center text-xs text-slate-500 font-bold select-none"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={pg}
                      type="button"
                      onClick={() => setPaginaAtual(pg)}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold transition-colors cursor-pointer border ${
                        pg === paginaAtual
                          ? "bg-urb-primary text-black border-urb-primary shadow-sm"
                          : "bg-urb-card-light border-urb-border text-slate-300 hover:text-white hover:border-urb-primary/40"
                      }`}
                    >
                      {pg}
                    </button>
                  )
                )}

                {/* Próxima Página */}
                <Tooltip content="Próxima página" position="top">
                  <button
                    type="button"
                    onClick={() => setPaginaAtual((prev) => Math.min(prev + 1, totalPaginas))}
                    disabled={paginaAtual === totalPaginas}
                    aria-label="Próxima página"
                    className="w-8 h-8 rounded-lg flex items-center justify-center bg-urb-card-light border border-urb-border text-slate-300 hover:text-white hover:border-urb-primary/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </Tooltip>

                {/* Última Página */}
                <Tooltip content="Última página" position="top">
                  <button
                    type="button"
                    onClick={() => setPaginaAtual(totalPaginas)}
                    disabled={paginaAtual === totalPaginas}
                    aria-label="Última página"
                    className="w-8 h-8 rounded-lg flex items-center justify-center bg-urb-card-light border border-urb-border text-slate-300 hover:text-white hover:border-urb-primary/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </button>
                </Tooltip>
              </div>

              {/* Salto Direto para Página */}
              {totalPaginas > 5 && (
                <div className="flex items-center gap-1.5 pl-2 sm:border-l border-urb-border/50 text-xs text-slate-400">
                  <span>Ir para:</span>
                  <input
                    type="number"
                    min={1}
                    max={totalPaginas}
                    value={paginaInput}
                    onChange={(e) => setPaginaInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleIrParaPagina(paginaInput);
                      }
                    }}
                    onBlur={() => handleIrParaPagina(paginaInput)}
                    className="w-14 px-1.5 py-1 text-center bg-urb-card-light border border-urb-border rounded-lg text-white text-xs focus:outline-none focus:border-urb-primary/60"
                  />
                  <span>de {totalPaginas}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal de Edição de Usuário */}
      <Modal
        isOpen={modalEditarAberto}
        onClose={handleFecharEditar}
        maxWidth="max-w-xl"
        header={
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
              <Edit2 className="w-4 h-4" />
            </div>
            <div>
              <span className="text-base font-bold text-white">Editar Usuário</span>
              {usuarioEditando && (
                <span className="block text-xs font-normal text-slate-400">
                  ID #{usuarioEditando.id} {usuarioEditando.username ? `(@${usuarioEditando.username})` : ""}
                </span>
              )}
            </div>
          </div>
        }
        footer={
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={salvandoEdicao}
              onClick={handleFecharEditar}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={salvandoEdicao}
              onClick={handleSalvarEdicao}
            >
              Salvar Alterações
            </Button>
          </>
        }
      >
        <form onSubmit={handleSalvarEdicao} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                Nome <span className="text-red-400">*</span>
              </label>
              <Input
                type="text"
                placeholder="Ex: João"
                value={formEdit.first_name}
                onChange={(e) => setFormEdit({ ...formEdit, first_name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                Sobrenome
              </label>
              <Input
                type="text"
                placeholder="Ex: Silva"
                value={formEdit.last_name}
                onChange={(e) => setFormEdit({ ...formEdit, last_name: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5">
              E-mail <span className="text-red-400">*</span>
            </label>
            <Input
              type="email"
              icon={Mail}
              placeholder="exemplo@email.com"
              value={formEdit.email}
              onChange={(e) => setFormEdit({ ...formEdit, email: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                Tipo / Perfil
              </label>
              <Select
                value={extractTipoUsuario(formEdit.tipo_usuario)}
                onChange={(e) => {
                  const val = extractTipoUsuario(e?.value ?? e?.target?.value);
                  setFormEdit((prev) => ({ ...prev, tipo_usuario: val }));
                }}
                optionLabel="label"
                options={[
                  { label: "Corredor (Padrão)", value: "Corredor" },
                  { label: "Organizador de Eventos", value: "Organizador" },
                  { label: "Equipe Staff / Administrador", value: "staff_urbrace" },
                ]}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5 flex items-center justify-between">
                <span>Telefone / Celular</span>
                {carregandoDadosUsuario && (
                  <span className="text-[10px] text-urb-primary font-normal animate-pulse">Carregando...</span>
                )}
              </label>
              <Input
                type="text"
                icon={Phone}
                placeholder="(00) 00000-0000"
                maxLength={15}
                value={formEdit.telefone}
                onChange={(e) => setFormEdit({ ...formEdit, telefone: maskPhone(e.target.value) })}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5 flex items-center justify-between">
              <span>CPF</span>
              {carregandoDadosUsuario && (
                <span className="text-[10px] text-urb-primary font-normal animate-pulse">Carregando...</span>
              )}
            </label>
            <Input
              type="text"
              icon={CreditCard}
              placeholder="000.000.000-00"
              maxLength={14}
              value={formEdit.cpf}
              onChange={(e) => setFormEdit({ ...formEdit, cpf: maskCPF(e.target.value) })}
            />
          </div>

          <div className="flex items-center justify-between p-3.5 bg-urb-card-light/40 border border-urb-border rounded-xl mt-1">
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-white">Status da Conta</span>
              <span className="text-xs text-slate-400">
                {formEdit.is_active ? "Conta ativa e habilitada para acessar o sistema" : "Conta inativa / bloqueada"}
              </span>
            </div>
            <Switch
              checked={formEdit.is_active}
              onChange={(checked) => setFormEdit({ ...formEdit, is_active: checked })}
            />
          </div>
        </form>
      </Modal>

      {/* Modal de Confirmação para Deletar Usuário */}
      <Modal
        isOpen={modalDeletarAberto}
        onClose={handleFecharDeletar}
        maxWidth="max-w-md"
        header={
          <div className="flex items-center gap-2 text-red-400">
            <div className="w-8 h-8 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-red-400" />
            </div>
            <span className="text-base font-bold text-white">Excluir Conta do Usuário</span>
          </div>
        }
        footer={
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={deletandoConta}
              onClick={handleFecharDeletar}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={deletandoConta}
              onClick={handleConfirmarDelecao}
            >
              Excluir Definitivamente
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3 py-1">
          <p className="text-sm text-slate-300">
            Tem certeza que deseja excluir permanentemente a conta de:
          </p>
          {usuarioDeletando && (
            <div className="p-3 bg-urb-card-light border border-urb-border rounded-xl flex flex-col gap-1">
              <span className="text-sm font-bold text-white">
                {[usuarioDeletando.first_name, usuarioDeletando.last_name].filter(Boolean).join(" ") || usuarioDeletando.username}
              </span>
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Mail className="w-3 h-3 text-slate-500" />
                {usuarioDeletando.email || "Sem e-mail"}
              </span>
              <span className="text-xs text-slate-500">
                ID #{usuarioDeletando.id} • Perfil: {usuarioDeletando.tipo_usuario || "Corredor"}
              </span>
            </div>
          )}
          <p className="text-xs text-red-400/90 bg-red-500/10 p-2.5 rounded-lg border border-red-500/20">
            Atenção: Esta ação não pode ser desfeita e removerá os acessos e dados associados a este usuário.
          </p>
        </div>
      </Modal>

      {/* Modal de Reset de Senha */}
      <Modal
        isOpen={modalResetAberto}
        onClose={handleFecharResetSenha}
        maxWidth="max-w-md"
        header={
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 shrink-0">
              <KeyRound className="w-4 h-4" />
            </div>
            <div>
              <span className="text-base font-bold text-white">Reset de Senha</span>
              <span className="block text-xs font-normal text-slate-400">
                Enviar link de recuperação de acesso
              </span>
            </div>
          </div>
        }
        footer={
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={enviandoReset}
              onClick={handleFecharResetSenha}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={enviandoReset}
              disabled={!usuarioReset?.email || enviandoReset}
              onClick={handleConfirmarResetSenha}
              className="gap-1.5 font-bold"
            >
              <KeyRound className="w-3.5 h-3.5" />
              <span>Enviar Link de Reset</span>
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3 py-1">
          <p className="text-sm text-slate-300">
            Deseja enviar instruções para redefinição de senha para este usuário?
          </p>

          {usuarioReset && (
            <div className="p-3 bg-urb-card-light border border-urb-border rounded-xl flex flex-col gap-1">
              <span className="text-sm font-bold text-white">
                {[usuarioReset.first_name, usuarioReset.last_name].filter(Boolean).join(" ") || usuarioReset.username}
              </span>
              <span className="text-xs text-slate-300 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                <span className={usuarioReset.email ? "text-white font-medium" : "text-amber-400 italic"}>
                  {usuarioReset.email || "Sem e-mail cadastrado"}
                </span>
              </span>
              <span className="text-xs text-slate-500 mt-0.5">
                ID #{usuarioReset.id} • Perfil: {usuarioReset.tipo_usuario || "Corredor"}
              </span>
            </div>
          )}

          {!usuarioReset?.email ? (
            <p className="text-xs text-amber-400 bg-amber-500/10 p-2.5 rounded-lg border border-amber-500/20">
              <strong>Atenção:</strong> Este usuário não possui um endereço de e-mail cadastrado. Edite o perfil do usuário para adicionar um e-mail antes de solicitar o reset de senha.
            </p>
          ) : (
            <p className="text-xs text-slate-400 bg-urb-card-light/50 p-2.5 rounded-lg border border-urb-border">
              Um e-mail seguro com o token de redefinição será enviado para <strong>{usuarioReset.email}</strong>, permitindo que o usuário cadastre uma nova senha com total segurança.
            </p>
          )}
        </div>
      </Modal>

      {/* Modal de Disparo Geral de E-mail */}
      <Modal
        isOpen={modalEmailAberto}
        onClose={handleFecharModalEmail}
        maxWidth="max-w-2xl"
        header={
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-urb-primary/10 border border-urb-primary/20 flex items-center justify-center text-urb-primary shrink-0">
              <Send className="w-4 h-4" />
            </div>
            <div>
              <span className="text-base font-bold text-white">Disparo Geral de E-mail</span>
              <span className="block text-xs font-normal text-slate-400">
                Envio em massa de comunicado oficial para os usuários cadastrados
              </span>
            </div>
          </div>
        }
        footer={
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={enviandoEmail}
              onClick={handleFecharModalEmail}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={enviandoEmail}
              disabled={
                enviandoEmail ||
                destinatariosAlvo.length === 0 ||
                !emailSubject.trim() ||
                !emailMessage.trim() ||
                !confirmouEnvioGeral
              }
              onClick={handleEnviarEmailGeral}
              className="gap-1.5 font-bold"
            >
              <Send className="w-3.5 h-3.5" />
              <span>
                {enviandoEmail
                  ? "Disparando e-mails..."
                  : `Disparar para ${destinatariosAlvo.length} usuário${destinatariosAlvo.length === 1 ? "" : "s"}`}
              </span>
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4 py-1">
          {/* Seletor de Público-Alvo */}
          <div>
            <label
              htmlFor="select-publico-alvo-email"
              className="block text-xs font-semibold text-slate-300 mb-1.5"
            >
              Público-Alvo / Destinatários
            </label>
            <select
              id="select-publico-alvo-email"
              aria-label="Público-Alvo / Destinatários"
              value={emailAudience}
              disabled={enviandoEmail}
              onChange={(e) => {
                setEmailAudience(e.target.value);
                setConfirmouEnvioGeral(false);
              }}
              className="w-full bg-urb-card-light border border-urb-border text-white text-sm rounded-xl px-3.5 py-2.5 transition-colors focus:outline-none focus:border-urb-primary focus:ring-1 focus:ring-urb-primary/30 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              <option value="todos" className="bg-urb-card-light text-white">
                Todos os Usuários Ativos ({totalAtivosComEmail} com e-mail)
              </option>
              <option value="corredor" className="bg-urb-card-light text-white">
                Apenas Corredores / Atletas ({totalCorredoresComEmail})
              </option>
              <option value="organizador" className="bg-urb-card-light text-white">
                Apenas Organizadores de Eventos ({totalOrganizadoresComEmail})
              </option>
              <option value="staff" className="bg-urb-card-light text-white">
                Apenas Equipe Staff ({totalStaffComEmail})
              </option>
              {globalFilter.trim() && (
                <option value="filtrados" className="bg-urb-card-light text-white">
                  Usuários da Busca Atual "{globalFilter}" ({totalFiltradosComEmail})
                </option>
              )}
            </select>

            {/* Informações dos Destinatários */}
            <div className="mt-2 p-2.5 bg-urb-card-light/60 border border-urb-border rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                <span className="text-slate-300">
                  Total de destinatários:{" "}
                  <strong className="text-white">{destinatariosAlvo.length}</strong> conta(s) com e-mail ativo
                </span>
              </div>
              {destinatariosAlvo.length > 0 && (
                <span className="text-[11px] text-slate-400 truncate max-w-xs">
                  Ex: {destinatariosAlvo.slice(0, 2).map((u) => u.email).join(", ")}
                  {destinatariosAlvo.length > 2 ? ` (+${destinatariosAlvo.length - 2} outros)` : ""}
                </span>
              )}
            </div>
          </div>

          {/* Assunto do E-mail */}
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5">
              Assunto do E-mail <span className="text-red-400">*</span>
            </label>
            <Input
              type="text"
              icon={Mail}
              placeholder="Ex: Comunicado Importante - Novidades na Plataforma Urbrace"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              disabled={enviandoEmail}
              required
            />
          </div>

          {/* Abas Escrever vs Pré-visualizar */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-semibold text-slate-300">
                Conteúdo da Mensagem <span className="text-red-400">*</span>
              </label>
              <div className="flex items-center gap-1 bg-urb-card-light p-0.5 rounded-lg border border-urb-border">
                <button
                  type="button"
                  onClick={() => setAbaEmail("escrever")}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors cursor-pointer ${
                    abaEmail === "escrever"
                      ? "bg-urb-primary text-black"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  <FileText className="w-3 h-3" />
                  <span>Escrever</span>
                </button>
                <button
                  type="button"
                  onClick={() => setAbaEmail("preview")}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors cursor-pointer ${
                    abaEmail === "preview"
                      ? "bg-urb-primary text-black"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  <Eye className="w-3 h-3" />
                  <span>Pré-visualização</span>
                </button>
              </div>
            </div>

            {abaEmail === "escrever" ? (
              <div className="flex flex-col gap-1.5">
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  disabled={enviandoEmail}
                  rows={7}
                  placeholder={`Digite aqui o texto do e-mail...\n\nVocê pode separar os assuntos usando quebras de linha para formar parágrafos.`}
                  className="w-full p-3 bg-urb-card-light border border-urb-border rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-urb-primary/60 transition-colors font-sans leading-relaxed resize-y min-h-[140px]"
                />
                <span className="text-[11px] text-slate-500">
                  Dica: As quebras de linha são convertidas automaticamente em parágrafos e espaçamentos no template oficial da Urbrace.
                </span>
              </div>
            ) : (
              /* Pré-visualização com template Urbrace */
              <div className="rounded-xl border border-urb-border/80 overflow-hidden bg-[#1a1a1a] shadow-inner text-white">
                <div className="bg-[#222831] border-t-4 border-urb-primary p-4 sm:p-5">
                  <div className="text-center pb-3 border-b border-white/10">
                    <span className="text-sm font-black text-urb-primary tracking-wide uppercase">
                      Urbrace - Comunicado Geral
                    </span>
                    {emailSubject.trim() && (
                      <h4 className="text-sm font-bold text-white mt-1">
                        {emailSubject}
                      </h4>
                    )}
                  </div>
                  <div className="py-4 text-xs text-slate-300">
                    <p className="font-semibold text-white mb-3">
                      Olá <strong>[Nome do Usuário]</strong>,
                    </p>
                    {emailMessage.trim() ? (
                      <div
                        className="space-y-3 leading-relaxed text-slate-300 text-xs"
                        dangerouslySetInnerHTML={{
                          __html: formatMessageHtml(emailMessage)
                        }}
                      />
                    ) : (
                      <p className="italic text-slate-500">
                        O conteúdo digitado na aba "Escrever" aparecerá aqui formatado...
                      </p>
                    )}
                  </div>
                  <div className="pt-3 border-t border-white/10 text-center text-[10px] text-slate-500">
                    Equipe Urbrace | contato@urbrace.com.br
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Aviso de Confirmação & Checkbox de Segurança */}
          <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl flex flex-col gap-2 mt-1">
            <div className="flex items-start gap-2 text-amber-400">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-300/90 leading-tight">
                <strong>Atenção:</strong> Esta ação iniciará o envio assíncrono de e-mails para{" "}
                <strong className="text-white underline">{destinatariosAlvo.length} usuário(s)</strong>.
                Revise com atenção o assunto e a mensagem antes de confirmar.
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none pt-1 border-t border-amber-500/20">
              <input
                type="checkbox"
                id="check-confirmacao-disparo-geral"
                checked={confirmouEnvioGeral}
                onChange={(e) => setConfirmouEnvioGeral(e.target.checked)}
                disabled={enviandoEmail}
                className="w-4 h-4 rounded border-urb-border text-urb-primary bg-urb-card-light focus:ring-urb-primary/30 cursor-pointer"
              />
              <span className="text-xs font-semibold text-white">
                Confirmo o disparo geral deste e-mail para todos os destinatários selecionados
              </span>
            </label>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default StaffUsuarios;
